import { useState, useCallback } from "react";
import { Edit, Trash2 } from "react-feather";
import { useNavigate } from "react-router-dom";
import { api } from "../../config/Api";
import { ActivateDeactivatePositionInterface, PositionInterface, PositionTableInterface } from "../../helper/interface";
import usePathName from "../../hooks/usePathName";
import useTable from "../../hooks/useTable";
import CustomTable from "../table/CustomTable";
import { onActivateDeactivatePosition, onDeletePosition } from "../../service/position";
import { useDispatch } from "react-redux";
import SearchForm from "../table/SearchForm";
import DeleteModal from "../modal/DeleteModal";
import { Tooltip } from "react-tooltip";
import IndustryTabs, { IndustrySectionTab } from "../industry/IndustryTabs";
import Industry from "../industry/Industry";

const Position = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<IndustrySectionTab>("industry");
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState<PositionInterface | null>(null);
    const dispatch = useDispatch();
    usePathName([{ pathName: "Industry", href: "/position" }]);

    const { fetchApi, loading, tableOffset, error, tableData, onSetOrderBy, onSearch, currentOrder, resetSearch } =
        useTable<PositionTableInterface>("position", api.admin.getAllPosition);

    const onDeleteClick = useCallback((record: PositionTableInterface) => {
        setPosition(record);
        setIsOpen(true);
    }, []);

    const positionCols = [
        {
            title: "#",
            key: "positionId",
            render: (record: PositionTableInterface, srNo: number) => {
                return <span>{srNo}</span>;
            },
        },
        {
            title: "POSITION",
            key: "name",
            sortable: true,
            render: (record: PositionTableInterface) => {
                return <span>{record.name}</span>;
            },
        },
        {
            title: "STATUS",
            key: "isActive",
            render: (record: PositionTableInterface) => {
                const payload = {
                    positionId: record.positionId,
                    isActive: !record.isActive,
                };
                return (
                    <div className="form-switch form-check flex justify-center flex-col">
                        <input
                            id="isActive"
                            name="isActive"
                            onChange={() => onActivateDeactivate(payload)}
                            checked={record.isActive}
                            className={"form-check-input"}
                            type="checkbox"
                        />
                    </div>
                );
            },
        },
        {
            title: "ACTION",
            key: "action",
            render: (record: PositionTableInterface) => {
                return (
                    <>
                        <div className="flex justify-center items-center text-primary">
                            <Tooltip id="simpleTooltipEditDeletePosition" place="top" />
                            <div
                                className="flex items-center mr-3 cursor-pointer"
                                data-tooltip-id="simpleTooltipEditDeletePosition"
                                data-tooltip-content={"Edit position"}
                                onClick={() => navigate(`/position/edit/${record.positionId}`)}
                            >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                            </div>
                            <div
                                className="flex items-center text-danger cursor-pointer"
                                data-tooltip-id="simpleTooltipEditDeletePosition"
                                data-tooltip-content={"Delete position"}
                                onClick={() => onDeleteClick(record)}
                            >
                                <Trash2 className="w-4 h-4 mr-1" /> Delete
                            </div>
                        </div>
                    </>
                );
            },
        },
    ];

    const onDelete = () => {
        if (position?.positionId) {
            onDeletePosition(position.positionId, dispatch, fetchApi);
            setIsOpen(false);
        }
    };

    const onActivateDeactivate = (payload: ActivateDeactivatePositionInterface) => {
        onActivateDeactivatePosition(payload, dispatch, fetchApi);
    };

    const handleCancel = () => {
        setIsOpen(false);
    };

    return (
        <>
            <IndustryTabs activeTab={activeTab} onChangeTabType={setActiveTab}>
                {activeTab === "industry" ? (
                    <Industry />
                ) : (
                    <>
                        <DeleteModal
                            handleCancel={handleCancel}
                            isOpen={isOpen}
                            onDelete={onDelete}
                            subTitle="Do you really want to delete this position?"
                        />

                        <div className="grid grid-cols-12 gap-6 mt-5">
                            <CustomTable
                                cols={positionCols}
                                data={tableData ?? []}
                                tableKey="positionId"
                                reload={fetchApi}
                                currentOrder={currentOrder}
                                error={error}
                                loading={loading}
                                tableOffset={tableOffset}
                                onSetOrderBy={onSetOrderBy}
                                component={
                                    <>
                                        <SearchForm resetSearch={resetSearch} onSearch={onSearch} searchPlaceHolder="Search by position" />
                                        <div className="flex mt-5 sm:mt-0 mr-2 sm:mr-0">
                                            <Tooltip id="simpleTooltipPosition" place="top" />
                                            <button
                                                className="btn btn-outline-primary w-full md:w-1/2 sm:w-auto"
                                                data-tooltip-id="simpleTooltipPosition"
                                                data-tooltip-content="Add new position"
                                                onClick={() => {
                                                    navigate("/position/add");
                                                }}
                                            >
                                                Add new
                                            </button>
                                        </div>
                                    </>
                                }
                            />
                        </div>
                    </>
                )}
            </IndustryTabs>
        </>
    );
};

export default Position;
